import { useEffect, useLayoutEffect, useState } from "react";

const GAP_BELOW_ANCHOR = 8;

export const elementAnchor = (testId) => ({
  contains: (target) => !!target.closest?.(`[data-testid="${testId}"]`),
  getRect: () => document.querySelector(`[data-testid="${testId}"]`)?.getBoundingClientRect(),
  key: testId
});

// Keeps the highlighted range itself, so the popover lines up with exactly what was highlighted.
export const selectionAnchor = (selection, key) => {
  const range = selection.rangeCount ? selection.getRangeAt(0).cloneRange() : null;
  const element = selection.anchorNode?.parentElement;
  return {
    contains: () => false,
    getRect: () => (range ?? element)?.getBoundingClientRect(),
    key
  };
};

const getPositionBelow = (anchorRect, panel) => {
  const container = panel.parentElement;
  const containerRect = container.getBoundingClientRect();
  const maxLeft = Math.max(container.clientWidth - panel.offsetWidth, 0);
  const centeredLeft = anchorRect.left - containerRect.left + anchorRect.width / 2 - panel.offsetWidth / 2;
  return {
    left: Math.min(Math.max(centeredLeft, 0), maxLeft),
    top: anchorRect.bottom - containerRect.top + GAP_BELOW_ANCHOR
  };
};

// Places the panel just below the anchor and closes it on a click anywhere else.
export default function useAnchoredPopover({ anchor, panelRef, onClose }) {
  const [position, setPosition] = useState(null);

  useLayoutEffect(() => {
    const placeBelowAnchor = () => {
      const anchorRect = anchor.getRect();
      if (anchorRect && panelRef.current) setPosition(getPositionBelow(anchorRect, panelRef.current));
    };
    placeBelowAnchor();
    window.addEventListener("resize", placeBelowAnchor);
    return () => window.removeEventListener("resize", placeBelowAnchor);
  }, [anchor.key]);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (panelRef.current?.contains(event.target) || anchor.contains(event.target)) return;
      onClose();
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, [anchor.key, onClose]);

  return position ?? { visibility: "hidden" };
}
